import { useTranslation } from "react-i18next";
import Card from "components/card/Card";
import { LocaleCurrency } from "helpers/Locale";
import { bookingLineAmount } from "helpers/segmentHourlyPrice";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import moment from "moment";
import React, { useEffect } from "react";
import { ClientPortalNavigation } from "../../ClientPortalNavigation";
import "./clientPortalReservationConfirmation.scss";
import { ButtonNew as Button } from "components/buttonNew";
import { useHistory, useParams } from "react-router";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import DataTable from "components/dataTable/DataTable";
import axios, { CancelToken } from "axios";
import { dequeue, enqueue, showError } from "redux/actions/ui";
import { GetReservationBooking } from "api/rpc/2024-04/customer/reservation";
import { StatusCode } from "api/protocols";
import { GetFacility } from "api/rpc/2024-04/customer/facility";
import { GetGuestReservationLocation, GetGuestReservationModule } from "api/rpc/2024-04/guest/reservation";

interface IParams {
  clientShortName: string;
  facilityShortName: string;
}

export default function ClientPortalReservationConfirmation() {
  const { t } = useTranslation();
  const { clientPortalStore } = useAppSelector(store => store);
  const { clientShortName, facilityShortName } = useParams<IParams>();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const urlString = window.location.search;
  const locationId = Number(new URLSearchParams(urlString).get("location_id"));
  const handle = new URLSearchParams(urlString).get("handle");
  const token = new URLSearchParams(urlString).get("token");

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (!clientPortalStore?.user) {
      void navigateToReservations();
    } else if (!clientPortalStore?.reservation) {
      void loadBooking(source.token);
    }

    return () => source.cancel();
  }, []);

  async function loadBooking(cancelToken: CancelToken) {
    dispatch(enqueue());
    const bookingRes = await GetReservationBooking({ token: token }, false, cancelToken);
    if (bookingRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting booking")));
      dispatch(dequeue());
      void navigateToReservations();
      return;
    }

    const moduleRes = await GetGuestReservationModule(
      { facility_short_name: facilityShortName, handle: handle },
      false,
      cancelToken,
    );
    if (moduleRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting module")));
      dispatch(dequeue());
      void navigateToReservations();
      return;
    }
    const [facilityRes, locationRes] = await Promise.all([
      GetFacility({ id: moduleRes?.data?.facility_id }, false, cancelToken),
      GetGuestReservationLocation({ id: locationId, facility_id: moduleRes?.data?.facility_id }, false, cancelToken),
    ]);

    if (facilityRes?.status !== StatusCode.OK || locationRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting facility")));
      dispatch(dequeue());
      void navigateToReservations();
      return;
    }
    dispatch(dequeue());
    dispatch(
      clientPortalUpdate({
        selectedFacility: facilityRes?.data[0],
        reservation: {
          ...clientPortalStore?.reservation,
          booking: bookingRes?.data[0],
          location: locationRes?.data[0],
          module: moduleRes?.data,
        },
      }),
    );
  }

  function navigateToReservations() {
    dispatch(clientPortalUpdate({ reservation: null }));
    history.push(
      `/portal/${clientShortName}/reservations/${facilityShortName ?? clientPortalStore?.selectedFacility?.short_name}`,
    );
  }

  return (
    <div className="client-portal-reservation-confirmation">
      <ClientPortalNavigation showNavOptions />
      <div className="client-portal-reservation-confirmation-facility-container">
        <Card>
          <Card.Section>
            <div className="client-portal-reservation-confirmation-flex-row">
              <span className="facility-logo">
                {clientPortalStore?.selectedFacility?.logo_source && (
                  <img
                    className="facility-logo"
                    src={clientPortalStore?.selectedFacility?.logo_source}
                    alt={t("Facility Logo")}
                  />
                )}
              </span>
              {clientPortalStore?.selectedFacility && (
                <div className="client-portal-reservation-confirmation-facility-container-group">
                  <p className="client-portal-reservation-confirmation-primary-text">
                    {clientPortalStore?.selectedFacility?.long_name}
                  </p>
                  <p className="client-portal-reservation-confirmation-secondary-text">
                    {clientPortalStore?.selectedFacility?.address_line_1}, {clientPortalStore?.selectedFacility?.city},{" "}
                    {clientPortalStore?.selectedFacility?.province_name}, {clientPortalStore?.selectedFacility?.postal}
                  </p>
                </div>
              )}
            </div>
          </Card.Section>
        </Card>
      </div>

      <div className="client-portal-reservation-confirmation-main-container">
        <Card>
          <Card.Section>
            <p className="client-portal-reservation-confirmation-primary-text">
              {clientPortalStore?.reservation?.booking?.status === "pending"
                ? t("Your reservation request has been submitted.")
                : t("Your reservation has been booked.")}
            </p>
            <p className="client-portal-reservation-confirmation-tertiary-text">
              {clientPortalStore?.reservation?.booking?.status === "pending"
                ? t(
                    "The facility will review your request. You will receive a confirmation email if it is accepted.",
                  )
                : `We'll send you a confirmation and receipt to ${
                    clientPortalStore?.reservation?.booking?.customer?.email ?? "your email"
                  } shortly`}
            </p>
            <p className="client-portal-reservation-confirmation-secondary-text">
              Confirmation Number:{" "}
              <span className="client-portal-reservation-confirmation-number">
                {clientPortalStore?.reservation?.booking?.confirmation_number}
              </span>
            </p>
          </Card.Section>
        </Card>
        <Card>
          <Card.Section>
            <p className="client-portal-reservation-confirmation-primary-text" style={{ marginBottom: "1rem" }}>
              {t('Booking Details')}
            </p>
            <div className="client-portal-reservation-confirmation-booking-details">
              <p className="client-portal-reservation-confirmation-tertiary-text">{t('Booked By')}</p>
              <div>
                <p className="reservation-booking-details-primary">
                  {clientPortalStore?.reservation?.booking?.customer?.full_name}
                </p>
                <p className="reservation-booking-details-primary">
                  {clientPortalStore?.reservation?.booking?.customer?.email}
                </p>
                <p className="reservation-booking-details-primary">
                  {clientPortalStore?.reservation?.booking?.customer?.phone}
                </p>
              </div>
              <p className="client-portal-reservation-confirmation-tertiary-text">{t('Order Summary')}</p>
              <div>
                <p className="reservation-booking-details-primary reservation-booking-details-bold">
                  {clientPortalStore?.selectedFacility?.long_name}
                </p>
                <p className="reservation-booking-details-primary">
                  {" "}
                  {moment(clientPortalStore?.reservation?.booking?.date).format("dddd")}{" "}
                  {moment(clientPortalStore?.reservation?.booking?.date).format("LL")} •{" "}
                  {moment(clientPortalStore?.reservation?.booking?.start_time, "hh:mm:ss").format("h:mm A")}{" "}
                  {clientPortalStore?.reservation?.endTime
                    ? " - ".concat(clientPortalStore?.reservation?.endTime)
                    : null}
                </p>
                <p className="reservation-booking-details-primary">
                  {clientPortalStore?.reservation?.location?.title} •{" "}
                  {clientPortalStore?.reservation?.booking?.quantity} Guest
                  {clientPortalStore?.reservation?.booking?.quantity > 1 ? "s" : ""}
                </p>
                <br />
                {clientPortalStore?.reservation?.bookedSegments && (
                  <DataTable
                    columns={[
                      { label: t("Time"), width: "50%" },
                      { label: t("Price"), width: "50%" },
                    ]}
                  >
                    {clientPortalStore?.reservation?.bookedSegments?.map((segment, segmentIndex) => {
                      if (segmentIndex % 2 === 0) {
                        return (
                          <tr key={segmentIndex}>
                            <td>{moment(segment?.start_time, "hh:mm:ss").format("h:mm A")}</td>
                            <td>
                              {bookingLineAmount(clientPortalStore?.reservation?.bookedSegments, segmentIndex, true) !=
                                null && (
                                <LocaleCurrency
                                  amount={
                                    bookingLineAmount(
                                      clientPortalStore?.reservation?.bookedSegments,
                                      segmentIndex,
                                      true,
                                    ) ?? undefined
                                  }
                                />
                              )}
                            </td>
                          </tr>
                        );
                      }
                    })}
                  </DataTable>
                )}
              </div>
              {clientPortalStore?.reservation?.bookedSegments && (
                <>
                  <p className="client-portal-reservation-confirmation-tertiary-text" style={{ marginBottom: "1rem" }}>
                    {t('Total Due at Course')}
                  </p>
                  <p className="reservation-booking-details-primary reservation-booking-details-bold">
                    <LocaleCurrency
                      amount={clientPortalStore?.reservation?.bookedSegments?.reduce(
                        (total, _segment, index) =>
                          index % 2 === 0
                            ? total +
                              (bookingLineAmount(clientPortalStore?.reservation?.bookedSegments, index, true) ?? 0)
                            : total,
                        0,
                      )}
                    />{" "}
                    (plus applicable taxes)
                  </p>
                </>
              )}
            </div>
            <div style={{ marginTop: "1rem" }}>
              <Button size="medium" type="primary" onClick={navigateToReservations}>
                {t('Back to Reservations')}
              </Button>
            </div>
          </Card.Section>
        </Card>
      </div>
    </div>
  );
}
